import { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Alert,
  Snackbar,
  Grid2 as Grid,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import placesService from '../services/placesService';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import placeServicesService from '../services/placeServicesService';
import placeProfessionalsService from '../services/placeProfessionalsService';
import usePlacesCRUD from '../hooks/usePlacesCRUD';
import { PlacesDataProvider } from '../contexts/PlacesDataContext';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useLocation } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PlaceForm from '../components/forms/PlaceForm';
import PlaceCardNormalized from '../components/places/PlaceCardNormalized';
import PlaceServicePaymentDialog from '../components/places/PlaceServicePaymentDialog';
import PlaceServicesManager from '../components/places/PlaceServicesManager';
import PlaceProfessionalsManager from '../components/places/PlaceProfessionalsManager';
import ProfessionalServicesDialog from '../components/places/ProfessionalServicesDialog';
import SmartWizard from '../components/wizard/SmartWizard';
import { getIconClass } from '../utils/iconUtils';
import PageLayout from '../components/ui/PageLayout';

/**
 * PlacesPage - Versão V3 com estrutura normalizada
 * 
 * Melhorias V3:
 * - API retorna estrutura normalizada { professionals, services, payments, places }
 * - Cache local de dados auxiliares (professionals, services, payments)
 * - Lookup O(1) via Maps ao invés de dados duplicados
 * - Redução de 70-90% no payload da API
 * - Dados auxiliares disponíveis durante toda a sessão da página
 */
export default function PlacesPage() {
  const location = useLocation();
  // ✅ Hook especializado para Places normalizados
  const { data, places, loading, error, reload, create, update, delete: deleteItem } = usePlacesCRUD(placesService);
  
  const { getIcon } = useThemeCustom();
  
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const deleteServiceDialog = useDialog();
  const deleteProfessionalDialog = useDialog();
  const paymentDialog = useDialog();
  const servicesDialog = useDialog();
  const professionalsDialog = useDialog();
  const professionalServicesDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const [showSmartWizard, setShowSmartWizard] = useState(false);

  // Obter dica contextual
  const hint = useContextualHint('places', { places, data });

  // Abrir wizard via querystring (?wizard=1 ou ?wizard=true ou ?create=1)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wizardParam = params.get('wizard');
    const createParam = params.get('create');
    if (wizardParam === '1' || wizardParam === 'true' || createParam === '1') {
      setShowSmartWizard(true);
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // ✅ Handlers
  const handleCreate = async (data) => {
    try {
      await create(data);
      formDialog.handleClose();
      showSuccess('Local criado com sucesso!');
    } catch (err) {
      showError('Erro ao criar local: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Local atualizado com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar local: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleDeleteFromForm = async (place) => {
    // Usar mesmo dialog de confirmação
    formDialog.handleClose();
    deleteDialog.handleOpen(place);
  };

  const handleRemoveService = async (place, placeService) => {
    // Abrir dialog de confirmação ao invés de window.confirm
    deleteServiceDialog.handleOpen({ place, placeService });
  };


  const handleRemoveProfessional = async (place, placeProfessional) => {
    // Abrir dialog de confirmação ao invés de window.confirm
    deleteProfessionalDialog.handleOpen({ place, placeProfessional });
  };


  const handleConfigureProfessionalServices = (place, placeProfessional) => {
    professionalServicesDialog.handleOpen({ place, placeProfessional });
  };

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando locais..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <PlacesDataProvider data={data}>
      <PageLayout
        themeKey="places"
        actionLabel="Novo Local"
        onAction={() => setShowSmartWizard(true)}
        hint={hint}
        onHintAction={() => setShowSmartWizard(true)}
        infoBanner={
          <Typography variant="body2">
            <strong>Gerenciamento visual:</strong> Todos os serviços e profissionais são exibidos nos cards.
            Clique no ícone de configuração ao lado de cada serviço para gerenciar formas de pagamento aceitas.
          </Typography>
        }
      >
      {/* Empty State */}
      {places.length === 0 ? (
        <EmptyState
          icon={getIconClass(getIcon('places').name)}
          title="Nenhum local cadastrado"
          message="Comece criando seu primeiro local para oferecer serviços"
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowSmartWizard(true)}>
              Criar Primeiro Local
            </Button>
          }
        />
      ) : (
        <Grid container spacing={3}>
          {places.map((place) => (
            <Grid size={{ xs: 12, md: 6, lg: 6 }} key={place.id}>
              <PlaceCardNormalized
                place={place}
                onEdit={(place) => formDialog.handleOpen(place)}
                onDelete={(place) => deleteDialog.handleOpen(place)}
                onConfigurePayments={(serviceLink) => {
                  // Adicionar placeId ao serviceLink antes de passar para o dialog
                  paymentDialog.handleOpen({ ...serviceLink, placeId: place.id });
                }}
                onManageServices={(place) => servicesDialog.handleOpen(place)}
                onManageProfessionals={(place) => professionalsDialog.handleOpen(place)}
                onRemoveService={handleRemoveService}
                onRemoveProfessional={handleRemoveProfessional}
                onConfigureProfessionalServices={handleConfigureProfessionalServices}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Place Creation Wizard - Removido em favor do SmartWizard */}

      {/* Place Form Dialog - Apenas para edição */}
      <PlaceForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        onDelete={handleDeleteFromForm}
        data={formDialog.data}
      />

      {/* Payment Configuration Dialog */}
      <PlaceServicePaymentDialog
        open={paymentDialog.open}
        onClose={paymentDialog.handleClose}
        placeService={paymentDialog.data}
        onUpdate={reload}
      />

      {/* Delete Place Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/places/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Delete Service from Place Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteServiceDialog.open}
        onClose={deleteServiceDialog.handleClose}
        onDeleted={() => {
          deleteServiceDialog.handleClose();
          showSuccess('Serviço removido do local com sucesso!');
          reload();
        }}
        deletePath={`/places/${deleteServiceDialog.data?.place?.id}/services/${deleteServiceDialog.data?.placeService?.serviceId}`}
        entityName={deleteServiceDialog.data?.placeService?.serviceName || deleteServiceDialog.data?.placeService?.service?.name}
      />

      {/* Delete Professional from Place Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteProfessionalDialog.open}
        onClose={deleteProfessionalDialog.handleClose}
        onDeleted={() => {
          deleteProfessionalDialog.handleClose();
          showSuccess('Profissional removido do local com sucesso!');
          reload();
        }}
        deletePath={`/places/${deleteProfessionalDialog.data?.place?.id}/professionals/${deleteProfessionalDialog.data?.placeProfessional?.id}`}
        entityName={deleteProfessionalDialog.data?.placeProfessional?.professionalName || deleteProfessionalDialog.data?.placeProfessional?.professional?.name}
      />

      {/* Place Services Manager */}
      <PlaceServicesManager
        open={servicesDialog.open}
        onClose={servicesDialog.handleClose}
        place={servicesDialog.data}
        onUpdate={reload}
      />

      {/* Place Professionals Manager */}
      <PlaceProfessionalsManager
        open={professionalsDialog.open}
        onClose={professionalsDialog.handleClose}
        place={professionalsDialog.data}
        onUpdate={reload}
      />

      {/* Professional Services Dialog */}
      <ProfessionalServicesDialog
        open={professionalServicesDialog.open}
        onClose={professionalServicesDialog.handleClose}
        placeProfessional={professionalServicesDialog.data?.placeProfessional}
        place={professionalServicesDialog.data?.place}
        onUpdate={reload}
      />

      {/* SmartWizard */}
      <SmartWizard
        open={showSmartWizard}
        wizardId="add-place"
        onClose={() => setShowSmartWizard(false)}
        onSuccess={() => {
          setShowSmartWizard(false);
          reload();
          showSuccess('Local criado com sucesso!');
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </PageLayout>
    </PlacesDataProvider>
  );
}
